import React, { useMemo, useState } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import EmailFile from "@mapmycustomers/shared/types/email/EmailFile";
import EmailResourceMetadata from "@mapmycustomers/shared/types/email/EmailResourceMetadata";
import {
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
  Resource,
} from "@mapmycustomers/shared/types/entity";
import parseEmailDynamicVariables from "@mapmycustomers/shared/util/email/parseEmailDynamicVariables";
import validateEmailResource from "@mapmycustomers/shared/util/email/validateEmailResource";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Alert, Button, EmailAttachedFile, EmailBody, EmailBodyEditor } from "@mapmycustomers/ui";

import { downloadEmailAttachedFile } from "@app/store/email/actions";
import useEmailResources from "@app/util/email/useEmailResources";
import getEmailDynamicVariablesInfo from "@app/util/fieldModel/util/getEmailDynamicVariablesInfo";

const messages = defineMessages({
  remove: {
    id: "email.emailBodyView.remove",
    defaultMessage: "Remove",
    description: "Email body view - Remove",
  },
  removeInvalidResourcesAlert: {
    id: "email.emailBodyView.removeInvalidResourcesAlert",
    defaultMessage: "Some resources cannot be found or have Restricted Access.",
    description: "Email body view - Remove invalid resources message",
  },
  removeNotFoundResourcesAlert: {
    id: "email.emailBodyView.removeNotFoundResourcesAlert",
    defaultMessage: "Some resources cannot be found.",
    description: "Email body view - Remove not found resources message",
  },
  removeRestrictedResourcesAlert: {
    id: "email.emailBodyView.removeRestrictedResourcesAlert",
    defaultMessage: "Some resources have Restricted Access.",
    description: "Email body view - Remove restricted resources message",
  },
});

interface EmailBodyViewProps {
  attachments: EmailFile[];
  body: string;
  entities?: EntitiesSupportedByEmailFeature[];
  entityType: EntityTypesSupportedByEmailFeature;
  onInvalidResourceLinksRemoved?: (value: string, resources: Resource[]) => void;
  removeInvalidResourcesButtonProps?: {
    disabled?: boolean;
    tooltip?: string;
  };
  // Omit it to suppress the invalid-resource alert entirely, for a surface that carries no
  // resources and so has nothing to tell the reader about them.
  removeResourcesAlertSuffix?: string;
  resources: Resource[];
  resourcesMetadata?: EmailResourceMetadata[];
}

const EmailBodyView = ({
  attachments,
  body,
  entities,
  entityType,
  onFileDownload,
  onInvalidResourceLinksRemoved,
  removeInvalidResourcesButtonProps,
  removeResourcesAlertSuffix,
  resources,
  resourcesMetadata,
}: EmailBodyViewProps & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const [editor, setEditor] = useState<EmailBodyEditor | undefined>();

  const {
    onResourceDownload,
    resourceGetter,
    resourceMetadataGetter,
    resources: resourcesMap,
  } = useEmailResources({
    editor,
    resources,
    resourcesMetadata,
  });

  const { hasNotFoundResources, hasRestrictedResources, invalidResourceIds } = useMemo(() => {
    return (resourcesMetadata ?? []).reduce(
      ({ hasNotFoundResources, hasRestrictedResources, invalidResourceIds }, resourceMetadata) => {
        const validationResult = validateEmailResource(resourcesMap.get(resourceMetadata.id));

        if (!validationResult.valid) {
          invalidResourceIds.push(resourceMetadata.id);
        }

        return {
          hasNotFoundResources: hasNotFoundResources || validationResult.notFound,
          hasRestrictedResources: hasRestrictedResources || validationResult.restricted,
          invalidResourceIds,
        };
      },
      {
        hasNotFoundResources: false,
        hasRestrictedResources: false,
        invalidResourceIds: [] as Resource["id"][],
      }
    );
  }, [resourcesMetadata, resourcesMap]);

  const [dynamicVariables] = useMemo(() => {
    return getEmailDynamicVariablesInfo({
      entities: entities ?? [],
      entityType: entityType,
      intl,
      parsedDynamicVariables: parseEmailDynamicVariables(body),
    });
  }, [intl, entities, entityType, body]);

  const attachedFiles: EmailAttachedFile[] = useMemo(() => {
    return attachments.map((file) => ({ id: file.id.toString(), file }));
  }, [attachments]);

  const handleRemove = useDynamicCallback(() => {
    editor!.removeResourceLinks(invalidResourceIds);
    const resourceIds = editor!.getResourceIds();
    onInvalidResourceLinksRemoved?.(
      editor!.getValue(),
      resourceIds.map((id) => resourcesMap.get(id)!)
    );
  });

  const renderInvalidResourcesAlert = () => {
    if (!invalidResourceIds.length || removeResourcesAlertSuffix === undefined) {
      return null;
    }

    let message: string;
    if (hasNotFoundResources && !hasRestrictedResources) {
      message = intl.formatMessage(messages.removeNotFoundResourcesAlert);
    } else if (!hasNotFoundResources && hasRestrictedResources) {
      message = intl.formatMessage(messages.removeRestrictedResourcesAlert);
    } else {
      message = intl.formatMessage(messages.removeInvalidResourcesAlert);
    }

    message += ` ${removeResourcesAlertSuffix}`;

    return (
      <Alert
        action={
          <Tooltip title={removeInvalidResourcesButtonProps?.tooltip}>
            <span>
              <Button
                disabled={removeInvalidResourcesButtonProps?.disabled}
                onClick={handleRemove}
                size="small"
                type="default"
              >
                {intl.formatMessage(messages.remove)}
              </Button>
            </span>
          </Tooltip>
        }
        message={message}
        showIcon
        type="warning"
      />
    );
  };

  return (
    <>
      <EmailBody
        attachedFiles={attachedFiles}
        dynamicVariables={dynamicVariables}
        entityType={entityType}
        onFileDownload={(attachedFile) => onFileDownload({ attachedFile })}
        onFilePreview={(attachedFile) => onFileDownload({ attachedFile, preview: true })}
        onInit={setEditor}
        onResourceDownload={onResourceDownload}
        readOnly
        resourceGetter={resourceGetter}
        resourceMetadataGetter={resourceMetadataGetter}
        value={body}
      />
      {renderInvalidResourcesAlert()}
    </>
  );
};

const mapDispatchToProps = {
  onFileDownload: downloadEmailAttachedFile,
};

export default connect(null, mapDispatchToProps)(EmailBodyView);
